import { ComponentProps } from 'react';
import {
  Button,
  Confirm,
  DeleteWithConfirmButton,
  EditButton,
  SaveButton,
  useDeleteWithConfirmController,
  useRecordContext,
  useResourceContext,
} from 'react-admin';
import { Button as MuiButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

const withClassName = (baseClassName: string, className?: string) => (
  [baseClassName, className].filter(Boolean).join(' ')
);

type SaveButtonProps = ComponentProps<typeof SaveButton>;

export const BhvkSaveButton = ({ className, size = 'medium', ...props }: SaveButtonProps) => (
  <SaveButton
    color="primary"
    variant="contained"
    size={size}
    icon={<SaveIcon />}
    {...props}
    className={withClassName('BhvkSaveButton-root', className)}
  />
);

type DialogSaveButtonProps = ComponentProps<typeof MuiButton>;

export const BhvkDialogSaveButton = ({
  className,
  size = 'medium',
  ...props
}: DialogSaveButtonProps) => (
  <MuiButton
    color="primary"
    variant="contained"
    size={size}
    startIcon={<SaveIcon />}
    {...props}
    className={withClassName('BhvkSaveButton-root', className)}
  />
);

type DialogConfirmButtonProps = ComponentProps<typeof MuiButton>;

export const BhvkDialogConfirmButton = ({
  className,
  size = 'medium',
  ...props
}: DialogConfirmButtonProps) => (
  <MuiButton
    color="primary"
    variant="contained"
    size={size}
    {...props}
    className={withClassName('BhvkConfirmButton-root', className)}
  />
);

type BackButtonProps = Omit<ComponentProps<typeof Button>, 'label'> & {
  label?: ComponentProps<typeof Button>['label'];
};

export const BhvkBackButton = ({ className, size = 'medium', ...props }: BackButtonProps) => (
  <Button
    label="general.text.go_back"
    variant="contained"
    size={size}
    {...props}
    className={withClassName('BhvkBackButton-root', className)}
  />
);

type CancelButtonProps = Omit<ComponentProps<typeof Button>, 'label'> & {
  label?: ComponentProps<typeof Button>['label'];
};

export const BhvkCancelButton = ({
  className,
  size = 'medium',
  label = 'ra.action.cancel',
  ...props
}: CancelButtonProps) => (
  <Button
    label={label}
    variant="contained"
    size={size}
    {...props}
    className={withClassName('BhvkCancelButton-root', className)}
  />
);

type DialogCancelButtonProps = ComponentProps<typeof MuiButton>;

export const BhvkDialogCancelButton = ({
  className,
  size = 'medium',
  ...props
}: DialogCancelButtonProps) => (
  <MuiButton
    variant="contained"
    size={size}
    {...props}
    className={withClassName('BhvkCancelButton-root', className)}
  />
);

type TableActionButtonProps = ComponentProps<typeof Button>;

export const BhvkTableActionButton = ({ className, ...props }: TableActionButtonProps) => (
  <Button
    variant="text"
    size="small"
    {...props}
    className={withClassName('BhvkTableActionButton-root', className)}
  />
);

type TableEditButtonProps = ComponentProps<typeof EditButton>;

export const BhvkTableEditButton = ({ className, ...props }: TableEditButtonProps) => (
  <EditButton
    variant="text"
    size="small"
    {...props}
    className={withClassName('BhvkTableActionButton-root', className)}
  />
);

export const BhvkDeleteWithConfirmButton = ({
  className,
  confirmTitle = 'ra.message.delete_title',
  confirmContent = 'ra.message.delete_content',
  confirmColor = 'warning',
  icon = <></>,
  label = 'ra.action.delete',
  mutationMode = 'pessimistic',
  onClick,
  redirect = 'list',
  translateOptions,
  mutationOptions,
  color = 'error',
  successMessage,
  ...rest
}: ComponentProps<typeof DeleteWithConfirmButton>) => {

  const record = useRecordContext(rest);
  const resource = useResourceContext(rest);
  const {
    open,
    isPending,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
  } = useDeleteWithConfirmController({
    record,
    redirect,
    mutationMode,
    onClick,
    mutationOptions,
    resource,
    successMessage,
  });

  return (
    <>
      <Button
        onClick={handleDialogOpen}
        label={label}
        className={withClassName('ra-delete-button', className)}
        color={color}
        {...rest}
      >
        {icon}
      </Button>
      <Confirm
        isOpen={open}
        loading={isPending}
        title={confirmTitle}
        content={confirmContent}
        confirm="ra.action.delete"
        confirmColor={confirmColor}
        translateOptions={{ id: record?.id, ...translateOptions }}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );

};

type TableDeleteButtonProps = ComponentProps<typeof DeleteWithConfirmButton>;

export const BhvkTableDeleteButton = ({ className, ...props }: TableDeleteButtonProps) => (
  <BhvkDeleteWithConfirmButton
    variant="text"
    size="small"
    color="inherit"
    {...props}
    className={withClassName('BhvkTableActionButton-root BhvkTableActionButton-destructive', className)}
  />
);

export const BhvkTableDeleteWithConfirmButton = BhvkTableDeleteButton;
